import React, {useState, useEffect} from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import DrawerContents from './SideAppBarContents/DrawerContents';

const SideAppBar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState<boolean>(!isMobile);

    useEffect(() => {
        setOpen(!isMobile);
    }, [isMobile]);

    const drawerWidth = isMobile ? 'auto' : 240;

    return (
        <>
            {/* Mobile Menu Button */}
            {isMobile && (
                <Fade in={!open}>
                    <Box
                        sx={{
                            position: 'fixed',
                            marginLeft: { xs: 1, sm: 4 },
                            top: { xs: 12, sm: 16 },
                            left: { xs: 12, sm: 16 },
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open menu"
                            onClick={() => setOpen(true)}
                            sx={{
                                backgroundColor: 'background.paper',
                                boxShadow: 3,
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                                '&:hover': {
                                    backgroundColor: 'background.default',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                        </IconButton>
                    </Box>
                </Fade>
            )}

            {/* Mobile Temporary Drawer */}
            {isMobile && (
                <Drawer
                    anchor="left"
                    open={open}
                    variant="temporary"
                    onClose={() => setOpen(false)}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'background.navigation',
                            width: drawerWidth,
                            maxWidth: drawerWidth,
                            boxShadow: 8,
                        }
                    }}
                    sx={{
                        '& .MuiBackdrop-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <IconButton
                            onClick={() => setOpen(false)}
                            aria-label="close menu"
                            sx={{
                                color: 'text.primary',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <DrawerContents toggleDrawer={setOpen} />
                </Drawer>
            )}

            {/* Desktop Persistent Drawer */}
            {!isMobile && (
                <Drawer
                    anchor="left"
                    open={open}
                    variant="persistent"
                    PaperProps={{
                        sx: {
                            backgroundColor: 'background.navigation',
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 2,
                        }
                    }}
                    sx={{
                        width: open ? drawerWidth : 0,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        }
                    }}
                >
                    <DrawerContents toggleDrawer={setOpen} />
                </Drawer>
            )}
        </>
    );
};

export default SideAppBar; 